import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Sparkles, Save, Copy, Eraser, Share2, Library, User as UserIcon, Users2, PartyPopper, CircleDot, Clock4 } from "lucide-react";
import { Link } from "react-router-dom";
import Markdown from "../../components/Markdown";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const EVENT_TYPES = [
  { value: "private", label: "Private lesson", desc: "1-on-1 session", icon: UserIcon, group: false },
  { value: "clinic", label: "Clinic", desc: "Structured small-group drilling", icon: Users2, group: true },
  { value: "mixer", label: "Mixer / Social", desc: "Social play with rotating partners", icon: PartyPopper, group: true },
  { value: "open_play", label: "Open play", desc: "Drop-in court time, lightly coached", icon: CircleDot, group: true },
  { value: "available_slot", label: "Available slot", desc: "Open slot awaiting a student", icon: Clock4, group: false },
];

const FOCUS_PRESETS = [
  "3rd-shot drops",
  "Dinking consistency at the kitchen",
  "Resetting from transition zone",
  "Serve + return depth",
  "Stacking / poaching tactics",
  "ATP & Erne plays",
  "Communication & doubles strategy",
  "Footwork + split-step timing",
];

function toLocalISOString(date, timeStr) {
  const [h, m] = (timeStr || "17:00").split(":").map(Number);
  const d = new Date(date);
  d.setHours(h, m, 0, 0);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}:00`;
}

export default function PlanDraft() {
  const [students, setStudents] = useState([]);
  const [form, setForm] = useState({
    type: "private",
    student_id: "",
    focus: "",
    duration_mins: 60,
    skill_level: 3.0,
    group_size: 4,
    courts: 1,
    title: "",
    notes: "",
  });
  const [plan, setPlan] = useState("");
  const [planId, setPlanId] = useState(null);
  const [shareUrl, setShareUrl] = useState("");
  const [sharing, setSharing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveForm, setSaveForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    time: "17:00",
    location: "",
    price: 0,
    courts: 1,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/students").then(({ data }) => setStudents(data || [])).catch(() => {});
  }, []);

  const currentType = EVENT_TYPES.find((t) => t.value === form.type);
  const isGroup = currentType?.group;
  const selectedStudent = students.find((s) => s.user_id === form.student_id);

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const generate = async () => {
    setLoading(true);
    setShareUrl("");
    try {
      const body = {
        student_id: form.type === "private" && form.student_id ? form.student_id : null,
        focus: form.focus,
        duration_mins: Number(form.duration_mins) || 60,
        lesson_type: form.type,
        title: form.title,
        skill_level: Number(form.skill_level) || null,
        group_size: isGroup ? Number(form.group_size) || 4 : 1,
        courts: Number(form.courts) || null,
        notes: form.notes,
      };
      const { data } = await api.post("/ai/lesson-plan", body);
      setPlan(data.plan);
      setPlanId(data.plan_id || null);
      toast.success("Plan generated and saved to your library");
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI generation failed");
    }
    setLoading(false);
  };

  const share = async () => {
    if (!planId) return;
    setSharing(true);
    try {
      const { data } = await api.post(`/plans/${planId}/share`);
      const url = `${window.location.origin}${data.share_path}`;
      setShareUrl(url);
      try { await navigator.clipboard.writeText(url); toast.success("Share link copied"); }
      catch { toast.success("Share link ready"); }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create share link");
    }
    setSharing(false);
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(plan); toast.success("Copied to clipboard"); } catch (_) {}
  };

  const saveAsLesson = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        title: form.title || `${currentType.label} — ${form.focus || "General"}`,
        description: form.focus,
        scheduled_at: toLocalISOString(saveForm.date, saveForm.time),
        duration_mins: Number(form.duration_mins) || 60,
        type: form.type,
        student_id: form.type === "private" && form.student_id ? form.student_id : null,
        location: saveForm.location,
        price: Number(saveForm.price) || 0,
        max_students: isGroup ? Number(form.group_size) || 4 : 1,
        courts: Number(saveForm.courts) || Number(form.courts) || 1,
        lesson_plan: plan,
      };
      await api.post("/lessons", payload);
      toast.success("Saved to your calendar");
      setSaveOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    }
    setSaving(false);
  };

  // When opening the save dialog, pre-fill courts from the draft
  useEffect(() => {
    if (saveOpen) setSaveForm((f) => ({ ...f, courts: f.courts || Number(form.courts) || 1 }));
  }, [saveOpen, form.courts]);

  return (
    <div className="space-y-8" data-testid="plan-draft-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold">AI Playbook</div>
          <h1 className="font-display text-5xl font-black uppercase tracking-tighter mt-2">Draft a lesson plan</h1>
          <p className="text-sm text-[#A3A3A3] mt-3 max-w-2xl">
            Generate a plan for any event — clinic, mixer, open play, or a private lesson.
            Attach a student to tune drills to their DUPR level. Every plan is saved to your library automatically.
          </p>
        </div>
        <Link
          to="/coach/plans/library"
          className="btn-secondary px-3 py-2 text-xs inline-flex items-center gap-2 mt-2"
          data-testid="plan-library-link"
        >
          <Library size={14} /> Plan library
        </Link>
      </div>

      <div className="grid lg:grid-cols-[380px_1fr] gap-6">
        {/* Controls */}
        <div className="card-surface p-6 space-y-6 h-fit">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-bold mb-2">Event type</div>
            <div className="grid grid-cols-1 gap-1.5">
              {EVENT_TYPES.map((t) => {
                const I = t.icon;
                const active = form.type === t.value;
                return (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => setForm({ ...form, type: t.value })}
                    className={`text-left flex items-center gap-3 p-3 border transition-colors ${active ? "border-[#38BDF8] bg-[#38BDF8]/10" : "border-white/10 hover:bg-white/[0.03]"}`}
                    data-testid={`plan-type-${t.value}`}
                  >
                    <I size={14} className={active ? "text-[#38BDF8]" : "text-white/70"} aria-hidden="true" />
                    <div className="min-w-0">
                      <div className={`text-xs font-bold uppercase tracking-wider ${active ? "text-[#38BDF8]" : "text-white"}`}>{t.label}</div>
                      <div className="text-[10px] text-[#A3A3A3]">{t.desc}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {form.type === "private" && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-bold">Student (optional)</div>
              <select
                value={form.student_id}
                onChange={setField("student_id")}
                className="w-full bg-transparent border-0 border-b border-white/20 focus:border-[#38BDF8] outline-none py-2 text-sm mt-1"
                data-testid="plan-student-select"
              >
                <option value="" className="bg-[#141414]">No specific student (generic plan)</option>
                {students.map((s) => (
                  <option key={s.user_id} value={s.user_id} className="bg-[#141414]">{s.name} · DUPR {s.skill_level}</option>
                ))}
              </select>
              {selectedStudent && (
                <div className="text-[11px] mt-2 flex items-center gap-2 flex-wrap" data-testid="plan-student-context">
                  <span className="px-2 py-0.5 border border-[#38BDF8]/40 text-[#38BDF8] uppercase tracking-wider text-[10px] font-bold">
                    DUPR {selectedStudent.skill_level || "—"}
                  </span>
                  <span className="text-[#38BDF8]">
                    Drills will be tuned to {selectedStudent.name}'s DUPR + goals.
                  </span>
                </div>
              )}
            </div>
          )}

          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-bold">Title (optional)</div>
            <input
              type="text" value={form.title} onChange={setField("title")}
              placeholder="e.g. Friday Dinking Clinic"
              className="w-full bg-transparent border-0 border-b border-white/20 focus:border-[#38BDF8] outline-none py-2 text-sm mt-1"
              data-testid="plan-title-input"
            />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-bold">Focus</div>
              <button type="button" onClick={() => setForm({ ...form, focus: "" })} className="text-[10px] text-[#38BDF8] hover:underline">Clear</button>
            </div>
            <textarea
              value={form.focus} onChange={setField("focus")} rows={4}
              placeholder="What should this session emphasize?  e.g. 'Build third-shot drop consistency under pace, then use it to reset transition zone defense. Watch for early backhand grip slip.'"
              className="w-full bg-transparent border border-white/15 focus:border-[#38BDF8] outline-none px-3 py-2 text-sm mt-1 resize-y"
              data-testid="plan-focus-input"
            />
            <div className="flex flex-wrap gap-1.5 mt-3">
              {FOCUS_PRESETS.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setForm({ ...form, focus: form.focus ? `${form.focus}\n• ${p}` : p })}
                  className={`text-[10px] px-2 py-1 border transition-colors border-white/10 text-[#A3A3A3] hover:border-[#38BDF8] hover:text-[#38BDF8]`}
                  data-testid={`plan-focus-preset-${p.slice(0, 10).replace(/\s+/g, "-").toLowerCase()}`}
                >+ {p}</button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-bold">Duration (min)</div>
              <input type="number" min="15" max="240" value={form.duration_mins} onChange={setField("duration_mins")}
                className="w-full bg-transparent border-b border-white/20 focus:border-[#38BDF8] outline-none py-2 text-sm"
                data-testid="plan-duration-input" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-bold"># Students</div>
              <input type="number" min="1" max="20" value={isGroup ? form.group_size : 1} onChange={setField("group_size")}
                disabled={!isGroup}
                className="w-full bg-transparent border-b border-white/20 focus:border-[#38BDF8] outline-none py-2 text-sm disabled:opacity-50"
                data-testid="plan-group-size" />
              <div className="text-[10px] text-[#A3A3A3] mt-1">{isGroup ? "Plan adapts to size + rotations." : "Private = 1-on-1."}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-bold">Courts available</div>
              <input type="number" min="1" max="20" value={form.courts} onChange={setField("courts")}
                className="w-full bg-transparent border-b border-white/20 focus:border-[#38BDF8] outline-none py-2 text-sm"
                data-testid="plan-courts-input" />
              <div className="text-[10px] text-[#A3A3A3] mt-1">Used to plan parallel stations.</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-bold">Skill (DUPR)</div>
              <input type="number" step="0.1" min="1.0" max="6.0" value={form.skill_level} onChange={setField("skill_level")}
                disabled={form.type === "private" && !!form.student_id}
                className="w-full bg-transparent border-b border-white/20 focus:border-[#38BDF8] outline-none py-2 text-sm disabled:opacity-50"
                data-testid="plan-skill-input" />
              <div className="text-[10px] text-[#A3A3A3] mt-1">{form.type === "private" && form.student_id ? "Auto from student profile." : "Sets level when no student is attached."}</div>
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-bold">
              Anything else the AI should know
            </div>
            <textarea
              value={form.notes}
              onChange={setField("notes")}
              rows={3}
              placeholder="Injuries, equipment limits, weather, goals — anything the structured fields don't capture."
              className="w-full bg-transparent border border-white/15 focus:border-[#38BDF8] outline-none px-3 py-2 text-sm mt-1 resize-y"
              data-testid="plan-notes-input"
            />
          </div>

          <button onClick={generate} disabled={loading} className="btn-primary w-full py-3 text-sm inline-flex items-center justify-center gap-2" data-testid="plan-generate-button">
            <Sparkles size={14} /> {loading ? "Drafting…" : (plan ? "Regenerate" : "Generate plan")}
          </button>
        </div>

        {/* Output */}
        <div className="card-surface p-6 min-h-[60vh]">
          {!plan ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-16" data-testid="plan-empty-state">
              <div className="w-14 h-14 border border-[#38BDF8]/50 grid place-items-center mb-4">
                <Sparkles size={20} className="text-[#38BDF8]" aria-hidden="true" />
              </div>
              <div className="font-display text-2xl font-black uppercase tracking-tight">Your plan shows up here</div>
              <p className="text-sm text-[#A3A3A3] mt-2 max-w-sm">
                Pick an event type and a focus, optionally attach a student, then tap Generate.
              </p>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold">Generated plan</div>
                  <h3 className="font-display text-2xl font-black uppercase tracking-tight mt-1">
                    {form.title || `${currentType.label}${form.focus ? ` · ${form.focus}` : ""}`}
                  </h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button onClick={copy} className="btn-secondary px-3 py-2 text-xs inline-flex items-center gap-2" data-testid="plan-copy-button">
                    <Copy size={12} /> Copy
                  </button>
                  <button
                    onClick={share}
                    disabled={!planId || sharing}
                    className="btn-secondary px-3 py-2 text-xs inline-flex items-center gap-2 disabled:opacity-50"
                    data-testid="plan-share-button"
                  >
                    <Share2 size={12} /> {sharing ? "Sharing…" : "Share link"}
                  </button>
                  <button onClick={() => { setPlan(""); setPlanId(null); setShareUrl(""); }} className="btn-secondary px-3 py-2 text-xs inline-flex items-center gap-2" data-testid="plan-clear-button">
                    <Eraser size={12} /> Clear
                  </button>
                  <button onClick={() => setSaveOpen(true)} className="btn-primary px-3 py-2 text-xs inline-flex items-center gap-2" data-testid="plan-save-button">
                    <Save size={12} /> Save to calendar
                  </button>
                </div>
              </div>

              {shareUrl && (
                <div className="border border-[#38BDF8]/40 bg-[#38BDF8]/5 p-3 flex items-center gap-3 flex-wrap" data-testid="plan-share-banner">
                  <Share2 size={14} className="text-[#38BDF8]" />
                  <div className="text-[11px] text-[#A3A3A3] uppercase tracking-wider">Public link</div>
                  <a
                    href={shareUrl} target="_blank" rel="noreferrer"
                    className="text-xs text-[#38BDF8] truncate flex-1 min-w-0 underline underline-offset-2"
                  >{shareUrl}</a>
                  <button
                    onClick={() => { navigator.clipboard.writeText(shareUrl).then(() => toast.success("Copied")); }}
                    className="text-[10px] uppercase tracking-wider text-[#38BDF8] hover:underline"
                    data-testid="plan-share-copy"
                  >Copy</button>
                </div>
              )}

              <textarea
                rows={16}
                value={plan}
                onChange={(e) => setPlan(e.target.value)}
                className="w-full bg-transparent border border-white/10 focus:border-[#38BDF8] outline-none p-4 text-sm font-mono"
                data-testid="plan-textarea"
              />

              <div className="border-t border-white/10 pt-5">
                <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-bold mb-3">Preview</div>
                <div className="prose prose-invert max-w-none text-sm" data-testid="plan-preview">
                  <Markdown>{plan}</Markdown>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Save dialog */}
      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="rounded-none bg-[#141414] border-white/10 max-w-md" data-testid="save-plan-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight text-2xl">Save as lesson</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveAsLesson} className="space-y-4">
            <p className="text-xs text-[#A3A3A3]">
              Creates a new <strong className="text-white">{currentType.label}</strong> on your calendar with this plan attached.
              {form.type === "private" && form.student_id && selectedStudent ? ` Assigned to ${selectedStudent.name}.` : " No student assigned — you can assign one later."}
            </p>
            <div className="grid grid-cols-2 gap-3">
              <SaveField label="Date" type="date" value={saveForm.date} onChange={(e) => setSaveForm({ ...saveForm, date: e.target.value })} testId="save-date" />
              <SaveField label="Time" type="time" value={saveForm.time} onChange={(e) => setSaveForm({ ...saveForm, time: e.target.value })} testId="save-time" />
            </div>
            <SaveField label="Location (optional)" value={saveForm.location} onChange={(e) => setSaveForm({ ...saveForm, location: e.target.value })} testId="save-location" />
            <div className="grid grid-cols-2 gap-3">
              <SaveField label="Price ($)" type="number" value={saveForm.price} onChange={(e) => setSaveForm({ ...saveForm, price: e.target.value })} testId="save-price" />
              <SaveField label="# Courts" type="number" value={saveForm.courts} onChange={(e) => setSaveForm({ ...saveForm, courts: e.target.value })} testId="save-courts" />
            </div>
            <button disabled={saving} className="btn-primary w-full py-3 text-sm" data-testid="save-plan-submit">
              {saving ? "Saving…" : "Save to calendar"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SaveField({ label, type = "text", value, onChange, testId }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-[#A3A3A3] font-bold">{label}</label>
      <input
        type={type} value={value} onChange={onChange}
        className="w-full bg-transparent border-0 border-b border-white/20 focus:border-[#38BDF8] outline-none py-2 text-sm"
        data-testid={testId}
      />
    </div>
  );
}
